import { PortableText, PortableTextComponents } from "@portabletext/react";
import { Image as SanityImage } from "sanity";
import { urlFor } from "@/sanity/sanity-utils"; // Asegúrate de tener una función para construir la URL de la imagen

const hasFootnotes = (content: any): boolean => {
  // Verificar si content está definido y es un array
  if (!Array.isArray(content)) {
    return false;
  }

  // Buscar footnotes en el contenido
  return content.some((block: any) => {
    if (block._type === "block" && block.markDefs) {
      return block.markDefs.some((def: any) => def._type === "footnote");
    }
    return false;
  });
};

export const portableBlockComponents = (content: any): PortableTextComponents => {
  // Verificar si hay footnotes en el contenido completo
  const hasAnyFootnotes = hasFootnotes(content);

  return {
    block: {
      // Componente para párrafos normales
      normal: ({ children, value }) => {
        // Extraer las footnotes del párrafo actual
        const footnotes = value.children
          .map((child: any) => {
            if (child.marks && child.marks.length > 0) {
              return child.marks
                .map((mark: string) => {
                  // Buscar la anotación correspondiente en markDefs
                  const annotation = value.markDefs?.find(
                    (def: any) => def._key === mark
                  );
                  // Verificar si es una footnote
                  if (annotation && annotation._type === "footnote") {
                    return annotation;
                  }
                  return null;
                })
                .filter(Boolean); // Eliminar valores nulos
            }
            return null;
          })
          .flat()
          .filter(Boolean); // Eliminar valores nulos

        return (
          <div className={hasAnyFootnotes ? 'flexNotes' : 'blockNotes'}>
            <p>{children}</p>
            {/* Renderizar el contenedor de footnotes si hay footnotes en el contenido */}
            {hasAnyFootnotes && (
              <div className="footnotes">
                {footnotes.length > 0 ? (
                  footnotes.map((fn: any, index: number) => (
                    <small key={index}>
                      <span>{fn.number}.</span>
                      <PortableText
                        value={fn.text} // fn.text es un Portable Text
                        components={portableBlockComponents(fn.text)} // Usar los mismos componentes personalizados
                      />
                    </small>
                  ))
                ) : (
                  // Contenedor vacío si no hay footnotes en este párrafo
                  <div style={{ display: "none" }} />
                )}
              </div>
            )}
          </div>
        );
      },
      // Otros estilos de bloque
      h1: ({ children }) => <h1>{children}</h1>,
      h2: ({ children }) => <h2>{children}</h2>,
      h3: ({ children }) => <h3>{children}</h3>,
      h4: ({ children }) => <h4>{children}</h4>,
      h5: ({ children }) => <h5>{children}</h5>,
      h6: ({ children }) => <h6>{children}</h6>,
      blockquote: ({ children }) => <blockquote>{children}</blockquote>,
    },
    marks: {
      strong: ({ children }) => <strong>{children}</strong>,
      em: ({ children }) => <em>{children}</em>,
      annotationLinkExternal: ({ value, children }: any) => {
        const { url, newWindow  } = value;
        return (
          <a href={url} target={newWindow ? "_blank" :  "_self"} rel="noopener noreferrer">
            {children}
          </a>
        );
      },
      annotationLinkEmail: ({ value, children }: any) => {
        const { email } = value;
        return (
          <a href={`mailto:${email}`}  rel="noopener noreferrer">
            {children}
          </a>
        );
      },
      footnote: ({ value, children }: any) => {
        // Renderizar la footnote junto al texto
        return (
          <span>
            {children}
            <span className={'numberFoot'}>{'('}{value.number}{')'}</span> {/* Muestra el número de la footnote */}
          </span>
        );
      },
    },
    types: {
      image: ({ value }: { value: SanityImage }) => {
        // Renderizar la imagen
        return (
          <div className={hasAnyFootnotes ? 'flexNotes' : 'blockNotes'}>
          {hasAnyFootnotes ? <div>
            <figure>
              <img
                src={urlFor(value).url()} // Usa tu función para construir la URL de la imagen
                alt={value.alt || "Imagen"}
              />
              {value.caption && <figcaption>{value.caption}</figcaption>}
            </figure>
          </div> : <figure>
            <img
              src={urlFor(value).url()} // Usa tu función para construir la URL de la imagen
              alt={value.alt || "Imagen"}
            />
            {value.caption && <figcaption>{value.caption}</figcaption>}
          </figure>}
          </div>
        );
      },
    },
    list: {
      bullet: ({ children }) => (
        <ul style={{ marginLeft: "calc(var(--margin) / 2)" }}>{children}</ul>
      ),
      number: ({ children }) => (
        <ol style={{ marginLeft: "calc(var(--margin) / 2)" }}>{children}</ol>
      ),
    },
    listItem: {
      bullet: ({ children }) => (
        <li style={{ listStyle: "disc" }}>{children}</li>
      ),
      number: ({ children }) => (
        <li style={{ listStyle: "decimal" }}>{children}</li>
      ),
    },
  };
};
